import { useId, useRef, useState } from 'react';
import type { CustomerDTO } from '@six-pockets/shared';
import { TextField, type TextFieldProps } from '@/components/ui';
import { useCustomerSuggest } from '../useCustomerSuggest';
import { CustomerSuggestList } from './CustomerSuggestList';

interface CustomerNameFieldProps extends Omit<TextFieldProps, 'value' | 'onChange' | 'hint'> {
  /** The name shown. */
  value: string;
  /** The existing customer the name stands for; null for a typed name. */
  customerId: string | null;
  /** A typed name comes with null; a customer picked from the list with their id (and their name). */
  onChange: (name: string, customerId: string | null) => void;
  /** Shown under the field while the name is typed text. */
  hint?: string;
}

/**
 * A name field for forms: typing offers the existing customers that match, and picking one makes the name theirs.
 * With no match (or when the list is ignored) it is a plain typed name. Customers are never created here.
 */
export function CustomerNameField({ value, customerId, onChange, hint, ...field }: CustomerNameFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  /** The name is being typed in: the list is offered from the first keystroke until the field is left. */
  const [typing, setTyping] = useState(false);

  const pick = (c: CustomerDTO) => {
    onChange(c.name, c.id);
    setTyping(false);
  };
  const suggest = useCustomerSuggest(value, typing, pick);

  return (
    <>
      <TextField
        ref={inputRef}
        hint={customerId ? 'Existing customer' : hint}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={suggest.open}
        aria-controls={suggest.open ? listId : undefined}
        autoComplete="off"
        maxLength={80}
        {...field}
        value={value}
        onChange={(e) => onChange(e.target.value, null)}
        // Not in onChange: React skips that when the text ends up the same (the name selected and typed again).
        onInput={() => setTyping(true)}
        onBlur={() => setTyping(false)}
        onKeyDown={(e) => void suggest.onKeyDown(e)}
      />
      {suggest.open && (
        <CustomerSuggestList anchorRef={inputRef} id={listId} results={suggest.results} active={suggest.active} onPick={pick} onClose={suggest.close} />
      )}
    </>
  );
}
